import { useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href.ts';
import { experiments } from '@/utils/experiment/manifest.ts';
import { EnrollmentType, useExperiment } from '@/utils/experiment/use-experiment.ts';
import { getWacIntegrationUrl } from '@/utils/urls.ts';

import { flushLoomLandExposure } from './flush-loom-land-exposure';

// Choice CTAs return to signup. Remember the offer to prevent a redirect loop
// and repeated exposure calls in the same tab.
export const LOOM_LAND_CHOICE_OFFERED_KEY = 'loomLandChoiceOffered';

type ExposureDiagnosticReason =
	| 'assignment_indeterminate'
	| 'not_enrolled'
	| 'default_value'
	| 'already_offered'
	| 'exposure_attempted'
	| 'exposure_flush_abandoned';

const hasChoiceBeenOffered = (): boolean => {
	try {
		return window.sessionStorage.getItem(LOOM_LAND_CHOICE_OFFERED_KEY) === 'true';
	} catch {
		// Restricted storage must not block signup; the ref still guards this mount.
		return false;
	}
};

const markChoiceOffered = (): void => {
	try {
		window.sessionStorage.setItem(LOOM_LAND_CHOICE_OFFERED_KEY, 'true');
	} catch {
		// Continue when browser storage is unavailable.
	}
};

/** Mount only after account readiness so the first experiment read uses the updated identity. */
export const useLoomLandExposure = (): boolean => {
	const exposureHandledOnce = useRef(false);
	const mounted = useRef(false);
	const [redirectAbandoned, setRedirectAbandoned] = useState(false);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const reportedReasons = useRef(new Set<ExposureDiagnosticReason>());
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Keep the initial guard stable after the effect marks this visit as offered.
	const [choiceAlreadyOffered] = useState(hasChoiceBeenOffered);
	const loomLandOptIn = useExperiment(experiments.twcg2108LoomLandOptIn);
	const choosePath = useLocalizedHref('/try/cloud/loom/choose');

	useEffect(() => {
		if (exposureHandledOnce.current) {
			return;
		}
		const reportDecision = (reason: ExposureDiagnosticReason) => {
			if (reportedReasons.current.has(reason)) {
				return;
			}
			reportedReasons.current.add(reason);

			try {
				createAnalyticsEvent({
					action: 'evaluated',
					actionSubject: 'loomLandOptInExposure',
					eventType: 'operational',
					attributes: {
						experimentName: experiments.twcg2108LoomLandOptIn.experimentName,
						reason,
						enrollmentType: loomLandOptIn.type,
						// A fallback control value is not a resolved control assignment.
						cohort:
							loomLandOptIn.type === EnrollmentType.ENROLLED && !loomLandOptIn.usingDefault
								? loomLandOptIn.value
								: null,
						usingDefault:
							loomLandOptIn.type === EnrollmentType.ENROLLED ? loomLandOptIn.usingDefault : null,
					},
				}).fire();
			} catch {
				// Diagnostic failures must not prevent exposure or signup navigation.
			}
		};

		// Wait for a definitively resolved arm before firing so exposure reflects
		// the post-identity bucketing rather than the default `control` fallback.
		if (loomLandOptIn.type !== EnrollmentType.ENROLLED) {
			reportDecision(
				loomLandOptIn.type === EnrollmentType.INDETERMINATE
					? 'assignment_indeterminate'
					: 'not_enrolled',
			);
			return;
		}
		if (loomLandOptIn.usingDefault !== false) {
			reportDecision('default_value');
			return;
		}

		exposureHandledOnce.current = true;

		if (choiceAlreadyOffered) {
			reportDecision('already_offered');
			return;
		}

		// This records the attempt, not delivery or acceptance by the exposure backend.
		reportDecision('exposure_attempted');
		if (loomLandOptIn.value === 'control') {
			loomLandOptIn.fireExposure();
			markChoiceOffered();
			return;
		}

		// Capture the destination now, but do not unload the document while the
		// cross-origin exposure request (including its preflight) is in flight.
		const destination = `${getWacIntegrationUrl()}${choosePath}${window.location.search}`;
		void flushLoomLandExposure(
			experiments.twcg2108LoomLandOptIn.experimentName,
			loomLandOptIn.fireExposure,
		).then((flushed) => {
			if (!mounted.current) {
				return;
			}
			if (!flushed) {
				// Keep the logger alive to retry and fall back to the signup form.
				// Do not mark this offer complete or redirect on a late resolution.
				reportDecision('exposure_flush_abandoned');
				setRedirectAbandoned(true);
				return;
			}
			// This is a redirect-loop guard, not a delivery acknowledgement.
			markChoiceOffered();
			window.location.assign(destination);
		});
	}, [loomLandOptIn, choosePath, choiceAlreadyOffered, createAnalyticsEvent]);

	return (
		!redirectAbandoned &&
		!choiceAlreadyOffered &&
		loomLandOptIn.type === EnrollmentType.ENROLLED &&
		loomLandOptIn.usingDefault === false &&
		loomLandOptIn.value !== 'control'
	);
};
